interface EventBonusCardPreviewProps {
  eventName: string;
  bonusPoints: number | null;
  /** Plural del nombre de los puntos del tenant, ej. "Puntos", "Estrellas". */
  currencyPlural: string;
}

/** La chispa dorada de la banda: estrella de cuatro puntas, el mismo trazo que usa la app. */
const SPARK_PATH = "M12 0 C12 7 17 12 24 12 C17 12 12 17 12 24 C12 17 7 12 0 12 C7 12 12 7 12 0 Z";

/**
 * Cómo se ve el bonus en la card del evento de la app: la banda de "puntos extra" pegada al pie
 * de la foto (ver `EventBonusBand` en el repo mobile). El degradé usa el verde de marca del BO
 * —no el color real del tenant— y la foto es un bloque de color: al preview solo le toca mostrar
 * la banda, no la imagen del evento.
 */
export const EventBonusCardPreview = ({
  eventName,
  bonusPoints,
  currencyPlural,
}: EventBonusCardPreviewProps) => {
  const hasBonus = bonusPoints != null && bonusPoints > 0;

  return (
    <div
      className="surface-card shadow-1 flex flex-column"
      style={{ borderRadius: "var(--radius-lg)", overflow: "hidden", maxWidth: "360px" }}
    >
      {/* La foto del evento es un bloque de color: lo justo para que la banda se apoye encima
          como en la app. */}
      <div style={{ position: "relative", height: "132px", backgroundColor: "var(--surface-300)" }}>
        {hasBonus && (
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              padding: "0 12px",
              // Degradé de marca: oscuro a la izquierda (texto claro) y claro a la derecha (el
              // puntaje oscuro), igual que la banda de la app.
              background: "linear-gradient(90deg, var(--brand-dark) 0%, var(--brand-bg-light) 100%)",
            }}
          >
            <span className="flex align-items-center" style={{ gap: "7px", minWidth: 0 }}>
              {/* Dorada a propósito: el único acento que no es de la marca, para que la chispa
                  se lea igual sobre cualquier color. */}
              <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
                <path d={SPARK_PATH} fill="#F5C451" strokeLinejoin="round" />
              </svg>
              <span
                style={{
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Asistí y sumá {currencyPlural}
              </span>
            </span>
            <span style={{ color: "var(--brand-dark)", fontWeight: 700, fontSize: "13px", flexShrink: 0 }}>
              +{bonusPoints}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-column" style={{ padding: "var(--space-16)", gap: "var(--space-12)" }}>
        <span className="text-900 font-semibold" style={{ overflowWrap: "anywhere" }}>
          {eventName.trim() || "Nombre del evento"}
        </span>
        {!hasBonus && (
          <small className="text-color-secondary">Ingresá los puntos para ver la banda.</small>
        )}
      </div>
    </div>
  );
};
